import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Stack,
  Typography,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import crmPlusService from '../../services/crmPlusService';

/**
 * Drop-in AI panel for a customer or opportunity page. Never fabricates —
 * every string rendered here comes straight from a crm-ai/* response. When
 * the backend has no model key configured (`getAiStatus` -> available:false)
 * this renders a plain "not configured" notice instead of buttons that would
 * only fail.
 */

export type CrmAiMode = 'customer' | 'opportunity';
type FollowUpTone = 'formal' | 'friendly' | 'urgent';

export interface CrmAiAssistProps {
  mode: CrmAiMode;
  id: number;
}

type ActionKey = 'summary' | 'meetingBrief' | 'dealSummary' | 'followUp';

interface AiResult {
  available: boolean;
  text?: string;
  reason?: string;
}

const ACTION_LABEL: Record<ActionKey, string> = {
  summary: 'Summarize',
  meetingBrief: 'Meeting brief',
  dealSummary: 'Summarize deal',
  followUp: 'Draft follow-up',
};

const CrmAiAssist: React.FC<CrmAiAssistProps> = ({ mode, id }) => {
  const [statusLoading, setStatusLoading] = useState(true);
  const [available, setAvailable] = useState<boolean | null>(null);

  const [activeAction, setActiveAction] = useState<ActionKey | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AiResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [tone, setTone] = useState<FollowUpTone>('formal');

  useEffect(() => {
    let cancelled = false;
    setStatusLoading(true);
    crmPlusService
      .getAiStatus()
      .then((res: any) => {
        if (!cancelled) setAvailable(!!res?.available);
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Switching entity/mode invalidates whatever was rendered for the last one.
  useEffect(() => {
    setResult(null);
    setRunError(null);
    setActiveAction(null);
    setCopied(false);
  }, [mode, id]);

  const run = (action: ActionKey) => {
    if (!id) return;
    setActiveAction(action);
    setRunning(true);
    setRunError(null);
    setResult(null);
    setCopied(false);

    let call: Promise<AiResult>;
    if (mode === 'customer' && action === 'summary') call = crmPlusService.aiCustomerSummary(id);
    else if (mode === 'customer' && action === 'meetingBrief') call = crmPlusService.aiMeetingBrief(id);
    else if (mode === 'opportunity' && action === 'dealSummary') call = crmPlusService.aiOpportunitySummary(id);
    else if (mode === 'opportunity' && action === 'followUp') call = crmPlusService.aiDraftFollowUp(id, tone);
    else {
      setRunning(false);
      return;
    }

    call
      .then((res) => setResult(res))
      .catch((e: any) => setRunError(e?.message || 'Failed to reach the AI assistant.'))
      .finally(() => setRunning(false));
  };

  const copyText = () => {
    if (!result?.text) return;
    navigator.clipboard
      ?.writeText(result.text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  const actions: ActionKey[] = useMemo(
    () => (mode === 'customer' ? ['summary', 'meetingBrief'] : ['dealSummary', 'followUp']),
    [mode]
  );

  if (statusLoading) {
    return (
      <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={18} />
        </Box>
      </Card>
    );
  }

  if (available === false) {
    return (
      <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" color="text.disabled">
            AI assistant not configured
          </Typography>
        </Stack>
      </Card>
    );
  }

  return (
    <Card elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            AI Assist
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <ButtonGroup size="small" variant="outlined" disabled={running}>
            {actions.map((a) => (
              <Button key={a} onClick={() => run(a)}>
                {running && activeAction === a ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                {ACTION_LABEL[a]}
              </Button>
            ))}
          </ButtonGroup>

          {mode === 'opportunity' && (
            <Select
              size="small"
              value={tone}
              onChange={(e) => setTone(e.target.value as FollowUpTone)}
              disabled={running}
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="formal">Formal</MenuItem>
              <MenuItem value="friendly">Friendly</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          )}
        </Stack>

        {running && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!running && runError && (
          <Typography variant="caption" color="error">
            {runError}
          </Typography>
        )}

        {!running && result && result.available === false && (
          <Typography variant="body2" color="text.disabled">
            {result.reason || 'The AI assistant could not produce a result for this item.'}
          </Typography>
        )}

        {!running && result && result.available !== false && result.text && (
          <>
            <Divider />
            <Box sx={{ position: 'relative' }}>
              <Tooltip title={copied ? 'Copied' : 'Copy'}>
                <IconButton
                  size="small"
                  onClick={copyText}
                  sx={{ position: 'absolute', top: -4, right: -4 }}
                  aria-label="Copy AI response"
                >
                  {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', pr: 4, lineHeight: 1.6 }}
              >
                {result.text}
              </Typography>
            </Box>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default CrmAiAssist;
